import PropTypes from 'prop-types'
import { isNil } from 'ramda'
import dayjs from 'dayjs'
import ActiveRecord from 'model/activeRecord'
import Member from 'model/person/member'
import { formatMoney } from 'utils/helpers'

const STATUS_DISPLAY = {
  pending: 'Pending',
  in_progress: 'In progress',
  action_needed: 'Action needed',
  resolved: 'Resolved'
}

export const CATEGORY_DISPLAY = {
  dental: 'Dental',
  medical: 'Medical',
  pharmacy: 'Pharmacy',
  vision: 'Vision'
}

class ClaimV2 extends ActiveRecord {
  constructor(init) {
    super(init)
    if (isNil(init)) return undefined
    this._admin_notes = init.admin_notes
    this._archived = init.archived
    this._amount_saved = init.amount_saved
    this._claim_category_name = init.claim_category_name
    this._date_of_service = init.date_of_service
    this._created_at = init.created_at
    this._member = new Member(init.member)
    this._medical_bills = init.medical_bills
    this._originally_billed = init.originally_billed
    this._request_notes = init.request_notes
    this._status = init.claim_status
    this._resolved_at = init.resolved_at
    this._tickets = init.tickets
    this._insurance_carrier = init.insurance_carrier
    this._insurance_member_id = init.insurance_member_id
    this._group_id = init.group_id
    this._focus_request = init.focus_request
    this._focus_request_other = init.focus_request_other
  }

  static shape = {
    id: PropTypes.string.isRequired,
    admin_notes: PropTypes.string,
    archived: PropTypes.bool,
    amount_saved: PropTypes.number,
    claim_category_name: PropTypes.string,
    date_of_service: PropTypes.string,
    created_at: PropTypes.ISO8601DateTime,
    member: PropTypes.instanceOf(Member),
    originally_billed: PropTypes.number,
    medical_bills: PropTypes.array,
    request_notes: PropTypes.string,
    status: PropTypes.string,
    resolved_at: PropTypes.string,
    tickets: PropTypes.array,
    insurance_carrier: PropTypes.string,
    insurance_member_id: PropTypes.string,
    group_id: PropTypes.string,
    focus_request: PropTypes.string,
    focus_request_other: PropTypes.string
  }

  get admin_notes() {
    return this._admin_notes || ''
  }

  get archived() {
    return this._archived || false
  }

  get amount_saved() {
    return this._amount_saved || 0
  }

  get claim_category_name() {
    return this._claim_category_name
  }

  get date_of_service() {
    return this._date_of_service
  }

  get created_at() {
    return dayjs(this._created_at).format('LLL')
  }

  get member() {
    return this._member
  }

  get display_status() {
    if (this.archived) return 'The Bill Advocacy is Archived.'

    const statusText = STATUS_DISPLAY[this.status] || this.status

    if (!this._resolved_at) return `The Bill Advocacy is ${statusText}.`

    const savedAmount = formatMoney(Number(this.amount_saved).toFixed(2))
    return `The Bill Advocacy is ${statusText}. The consumer was saved ${savedAmount}.`
  }

  get medical_bills() {
    return this._medical_bills || []
  }

  get originally_billed() {
    return this._originally_billed || 0
  }

  get request_notes() {
    return this._request_notes || ''
  }

  get status() {
    return this._status
  }

  get resolved_at() {
    return this._resolved_at
  }

  get tickets() {
    return this._tickets
  }

  get insurance_carrier() {
    return this._insurance_carrier
  }

  get insurance_member_id() {
    return this._insurance_member_id
  }

  get group_id() {
    return this._group_id
  }

  get focus_request() {
    return this._focus_request
  }

  get focus_request_other() {
    return this._focus_request_other
  }

  // Calculable
  get isPending() {
    return this.status === 'pending'
  }

  get isInProgress() {
    return this.status === 'in_progress'
  }

  get isActionNeeded() {
    return this.status === 'action_needed'
  }

  get isResolved() {
    return this.status === 'resolved'
  }

  get getClaimCategory() {
    return CATEGORY_DISPLAY[this._claim_category_name] || 'N/A'
  }

  get shouldBlockUI() {
    return this.isResolved || this.archived
  }

  get shouldBlockReadyToClose() {
    return !this.isResolved && !this.archived
  }
}

export default ClaimV2
